import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pageContentConfig } from '../content/pageContentConfig';
import {
  deletePageOverride,
  getPageOverride,
  upsertPageOverride
} from '../services/contentService';
import {
  deletePageImages,
  getPageImages,
  upsertPageImages,
  uploadPageImage
} from '../services/imageService';
import { pageImageConfig } from '../content/pageImageConfig';
import { deepMerge } from '../utils/deepMerge';

function formatFieldLabel(value) {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function setValueAtPath(target, path, newValue) {
  const next = structuredClone(target);
  let current = next;

  for (let i = 0; i < path.length - 1; i += 1) {
    current = current[path[i]];
  }

  current[path[path.length - 1]] = newValue;
  return next;
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedPageKey, setSelectedPageKey] = useState(pageContentConfig[0].key);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [editableContent, setEditableContent] = useState({});
  const [editableImages, setEditableImages] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImageKey, setUploadingImageKey] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const adminEmail = sessionStorage.getItem('adminEmail') || 'Admin';

  const selectedPage = useMemo(
    () => pageContentConfig.find((page) => page.key === selectedPageKey) || pageContentConfig[0],
    [selectedPageKey]
  );
  const languageOptions = useMemo(
    () => Object.keys(selectedPage.translations || {}),
    [selectedPage]
  );

  useEffect(() => {
    if (!languageOptions.includes(selectedLanguage)) {
      setSelectedLanguage(languageOptions[0] || 'en');
    }
  }, [languageOptions, selectedLanguage]);

  useEffect(() => {
    let active = true;

    async function loadEditorContent() {
      const defaultContent =
        selectedPage.translations[selectedLanguage] || selectedPage.translations.en || {};
      setIsLoading(true);
      setErrorMessage('');
      setStatusMessage('');

      try {
        const override = await getPageOverride(selectedPage.key, selectedLanguage);
        const merged = deepMerge(defaultContent, override || {});
        if (active) {
          setEditableContent(merged);
        }
      } catch {
        if (active) {
          setEditableContent(defaultContent);
          setErrorMessage('Could not load Supabase override. Showing default content.');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    loadEditorContent();

    return () => {
      active = false;
    };
  }, [selectedPage, selectedLanguage]);

  useEffect(() => {
    let active = true;

    async function loadImages() {
      const defaultImages = pageImageConfig[selectedPage.key] || {};
      try {
        const override = await getPageImages(selectedPage.key);
        if (active) {
          setEditableImages({ ...defaultImages, ...(override || {}) });
        }
      } catch {
        if (active) {
          setEditableImages(defaultImages);
        }
      }
    }

    loadImages();

    return () => {
      active = false;
    };
  }, [selectedPage.key]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminEmail');
    navigate('/admin');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSave = async () => {
    setErrorMessage('');
    setStatusMessage('');

    setIsSaving(true);
    try {
      await upsertPageOverride(selectedPage.key, selectedLanguage, editableContent);
      await upsertPageImages(selectedPage.key, editableImages);
      setStatusMessage('Saved. Website content updated.');
    } catch {
      setErrorMessage('Failed to save to Supabase. Please check your table and policies.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setErrorMessage('');
    setStatusMessage('');
    setIsSaving(true);
    try {
      await deletePageOverride(selectedPage.key, selectedLanguage);
      await deletePageImages(selectedPage.key);
      const defaultContent =
        selectedPage.translations[selectedLanguage] || selectedPage.translations.en || {};
      setEditableContent(defaultContent);
      setEditableImages(pageImageConfig[selectedPage.key] || {});
      setStatusMessage('Override removed. Page now uses default content.');
    } catch {
      setErrorMessage('Failed to reset override.');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (path, value) => {
    setEditableContent((previous) => setValueAtPath(previous, path, value));
  };

  const updateImageField = (key, value) => {
    setEditableImages((previous) => ({ ...previous, [key]: value }));
  };

  const handleImageUpload = async (imageKey, file) => {
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload a valid image file.');
      return;
    }

    setErrorMessage('');
    setStatusMessage('');
    setUploadingImageKey(imageKey);
    try {
      const publicUrl = await uploadPageImage({
        file,
        pageKey: selectedPage.key,
        imageKey
      });
      updateImageField(imageKey, publicUrl);
      setStatusMessage('Image uploaded. Click Save Changes to publish.');
    } catch {
      setErrorMessage('Image upload failed. Check bucket policies and try again.');
    } finally {
      setUploadingImageKey('');
    }
  };

  const renderField = (fieldKey, value, path = []) => {
    const currentPath = [...path, fieldKey];
    const fieldPathKey = currentPath.join('.');
    const label = formatFieldLabel(fieldKey);

    if (isPlainObject(value)) {
      return (
        <div key={fieldPathKey} className="admin-field-group">
          <h3>{label}</h3>
          <div className="admin-field-grid">
            {Object.entries(value).map(([nestedKey, nestedValue]) =>
              renderField(nestedKey, nestedValue, currentPath)
            )}
          </div>
        </div>
      );
    }

    if (Array.isArray(value)) {
      const primitiveArray = value.every(
        (item) =>
          typeof item === 'string' ||
          typeof item === 'number' ||
          typeof item === 'boolean' ||
          item === null
      );

      return (
        <div key={fieldPathKey} className="admin-form-field">
          <label htmlFor={fieldPathKey}>{label}</label>
          {primitiveArray ? (
            <textarea
              id={fieldPathKey}
              value={value.map((item) => String(item ?? '')).join('\n')}
              onChange={(event) => updateField(currentPath, event.target.value.split('\n'))}
              rows={Math.min(Math.max(value.length + 1, 4), 12)}
            />
          ) : (
            <textarea
              id={fieldPathKey}
              value={JSON.stringify(value, null, 2)}
              onChange={(event) => {
                try {
                  const parsed = JSON.parse(event.target.value);
                  updateField(currentPath, parsed);
                  setErrorMessage('');
                } catch {
                  setErrorMessage(`Invalid JSON in "${label}" array.`);
                }
              }}
              rows={10}
            />
          )}
        </div>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <div key={fieldPathKey} className="admin-form-field admin-checkbox-field">
          <label htmlFor={fieldPathKey}>{label}</label>
          <input
            id={fieldPathKey}
            type="checkbox"
            checked={value}
            onChange={(event) => updateField(currentPath, event.target.checked)}
          />
        </div>
      );
    }

    const asString = value === null || value === undefined ? '' : String(value);
    const useTextarea = asString.length > 100 || asString.includes('\n');

    return (
      <div key={fieldPathKey} className="admin-form-field">
        <label htmlFor={fieldPathKey}>{label}</label>
        {useTextarea ? (
          <textarea
            id={fieldPathKey}
            value={asString}
            onChange={(event) => updateField(currentPath, event.target.value)}
            rows={4}
          />
        ) : (
          <input
            id={fieldPathKey}
            type={typeof value === 'number' ? 'number' : 'text'}
            value={asString}
            onChange={(event) =>
              updateField(
                currentPath,
                typeof value === 'number' ? Number(event.target.value || 0) : event.target.value
              )
            }
          />
        )}
      </div>
    );
  };

  return (
    <div className="admin-layout">
      <header className="admin-mobile-header">
        <span className="admin-mobile-title">Admin Panel</span>
        <button
          type="button"
          className="admin-mobile-toggle"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          aria-label="Toggle admin menu"
          aria-expanded={isMobileMenuOpen}
        >
          Menu
        </button>
      </header>

      {isMobileMenuOpen && (
        <div className="admin-mobile-dropdown">
          {pageContentConfig.map((page) => (
            <button
              key={page.key}
              type="button"
              className="admin-nav-btn"
              onClick={() => {
                setSelectedPageKey(page.key);
                closeMobileMenu();
              }}
            >
              {page.label}
            </button>
          ))}
          <button type="button" className="admin-logout-btn" onClick={handleLogout}>Log Out</button>
        </div>
      )}

      <aside className="admin-sidebar">
        <h2 className="admin-sidebar-title">Admin Panel</h2>
        <p className="admin-sidebar-email">{adminEmail}</p>
        <nav className="admin-sidebar-nav">
          {pageContentConfig.map((page) => (
            <button
              key={page.key}
              type="button"
              className="admin-nav-btn"
              onClick={() => setSelectedPageKey(page.key)}
            >
              {page.label}
            </button>
          ))}
        </nav>
        <button type="button" onClick={handleLogout} className="admin-logout-btn">
          Log Out
        </button>
      </aside>

      <main className="admin-main">
        <div className="admin-welcome-card">
          <h1 className="admin-welcome-title">Website Content Manager</h1>
          <p className="admin-welcome-text">
            Select a page, edit text and image URLs, and save to update the live website.
          </p>
        </div>

        <div className="admin-editor-card">
          <div className="admin-editor-controls">
            <div className="admin-control-group">
              <label htmlFor="admin-page-select">Page</label>
              <select
                id="admin-page-select"
                value={selectedPageKey}
                onChange={(event) => setSelectedPageKey(event.target.value)}
              >
                {pageContentConfig.map((page) => (
                  <option key={page.key} value={page.key}>
                    {page.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="admin-control-group">
              <label htmlFor="admin-language-select">Language</label>
              <select
                id="admin-language-select"
                value={selectedLanguage}
                onChange={(event) => setSelectedLanguage(event.target.value)}
              >
                {languageOptions.map((lang) => (
                  <option key={lang} value={lang}>
                    {lang.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {errorMessage && <p className="admin-error-message">{errorMessage}</p>}
          {statusMessage && <p className="admin-success-message">{statusMessage}</p>}

          <div className="admin-visual-editor">
            {!isLoading &&
              Object.entries(editableContent).map(([fieldKey, fieldValue]) =>
                renderField(fieldKey, fieldValue)
              )}
          </div>

          <div className="admin-editor-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={handleSave}
              disabled={isSaving || isLoading}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleReset}
              disabled={isSaving || isLoading}
            >
              Reset to Default
            </button>
          </div>
        </div>
        <div className="admin-editor-card">
          <h2 className="admin-preview-title">Images</h2>
          {Object.keys(editableImages).length === 0 && (
            <p className="admin-welcome-text">No image fields mapped for this page yet.</p>
          )}
          <div className="admin-field-grid">
            {Object.entries(editableImages).map(([key, value]) => (
              <div key={key} className="admin-form-field">
                <label htmlFor={`image-${key}`}>{formatFieldLabel(key)}</label>
                <input
                  id={`image-${key}`}
                  type="text"
                  value={value}
                  onChange={(event) => updateImageField(key, event.target.value)}
                />
                <div className="admin-upload-row">
                  <label className="admin-upload-btn" htmlFor={`upload-${key}`}>
                    {uploadingImageKey === key ? 'Uploading...' : 'Upload Image'}
                  </label>
                  <input
                    id={`upload-${key}`}
                    type="file"
                    accept="image/*"
                    className="admin-upload-input"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      handleImageUpload(key, file);
                      event.target.value = '';
                    }}
                  />
                </div>
                {value && (
                  <img
                    src={value}
                    alt={key}
                    className="admin-image-thumb"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
